import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { Info } from 'lucide-react';
import KaskoSeo from './KaskoSeo';

const Kasko = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  
  const [formData, setFormData] = useState({
    tc: '',
    plaka: '',
    ruhsatNo: '',
    immLimit: '',
    ikameArac: '',
    telefon: '',
    kvkk: true
  });

  const agents = [
    { name: 'Kübra', title: 'Kasko Uzmanı', image: '💁‍♀️', testimonial: "Aracınız için en kapsamlı kasko tekliflerini sunuyorum." },
    { name: 'Murat', title: 'Kasko Danışmanı', image: '👨‍💼', testimonial: "Size özel kasko teklifleri için hazırım." },
    { name: 'Enes', title: 'Kasko Sorumlusu', image: '👨‍💼', testimonial: "En uygun kasko teklifini birlikte bulalım." },
    { name: 'Rukiye', title: 'Kasko Uzmanı', image: '👩‍💼', testimonial: "Aracınız için en iyi kasko teklifini hazırlayalım." }
  ];

  const [activeAgent, setActiveAgent] = useState(0);

  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * agents.length);
    setActiveAgent(randomIndex);

    const timer = setInterval(() => {
      setActiveAgent((prev) => (prev + 1) % agents.length);
    }, 3000);

    return () => clearInterval(timer);
  }, []);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.tc || formData.tc.length !== 11 || !/^\d+$/.test(formData.tc)) {
      errors.tc = 'Geçerli bir TC Kimlik Numarası giriniz';
    }

    if (!formData.plaka || formData.plaka.length < 5) {
      errors.plaka = 'Geçerli bir plaka giriniz';
    }

    if (!formData.ruhsatNo || formData.ruhsatNo.length < 5) {
      errors.ruhsatNo = 'Geçerli bir ruhsat numarası giriniz';
    }

    if (step === 2) {
      const phoneRegex = /^[0-9]{10,11}$/;
      if (!formData.telefon || !phoneRegex.test(formData.telefon.replace(/\D/g, ''))) {
        errors.telefon = 'Geçerli bir telefon numarası giriniz';
      }

      if (!formData.immLimit) {
        errors.immLimit = 'IMM limit seçimi zorunludur';
      }

      if (!formData.ikameArac) {
        errors.ikameArac = 'İkame araç süresi seçimi zorunludur';
      }

      if (!formData.kvkk) {
        errors.kvkk = 'KVKK metnini kabul etmelisiniz';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) {
      return;
    }
  
    setLoading(true);
  
    try {
      const response = await fetch(`${window.location.protocol}//${window.location.host}/kasko-teklif.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        throw new Error(data.message || 'Bir hata oluştu');
      }
  
      // Başarılı durumda teşekkür sayfasına yönlendir
      navigate(`/tesekkurler?teklifKodu=${data.teklifKodu}`);
      
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="overflow-x-hidden">
      <Helmet>
        <title>Kasko Sigortası - En Uygun Fiyatlarla | SigortamOlsun</title>
        <meta name="description" content="Aracınız için en uygun kasko sigortası tekliflerini anında karşılaştırın." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href="https://sigortamolsun.com/kasko" />
      </Helmet>

      <div className="px-4 py-8">
        <div className="max-w-md mx-auto">
          {/* Agent Kartları */}
          <div className="relative h-32 mb-4">
            {agents.map((agent, index) => (
              <div
                key={agent.name}
                className={`absolute w-full transition-all duration-500 transform
                  ${index === activeAgent ? 'scale-100 opacity-100' : 'scale-75 opacity-0'}
                  flex items-center gap-3 bg-blue-50 p-4 rounded-xl shadow-md border-2 border-blue-200`}
                style={{
                  transform: `translateX(${(index - activeAgent) * 100}%) scale(${index === activeAgent ? 1 : 0.75})`,
                  zIndex: index === activeAgent ? 10 : 0
                }}
              >
                <span className="text-2xl sm:text-3xl flex-shrink-0">{agent.image}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-blue-900">{agent.name}</div>
                  <div className="text-sm text-blue-600">{agent.title}</div>
                  <div className="text-sm italic text-gray-500 break-words">{agent.testimonial}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Bilgilendirme */}
          <div className="text-center mb-6">
            <h1 className="text-xl sm:text-2xl font-semibold mb-4 whitespace-pre-line">
              {'Merhaba,\nBen kasko uzmanı '}<span className="text-blue-600">{agents[activeAgent].name}</span>{'.\nAracınız için en iyi teklifi bulalım.'}
            </h1>
            <p className="text-gray-600 text-sm">
              {step === 1 ? 'Lütfen araç bilgilerinizi girin.' : 'Şimdi kasko teminatlarını seçelim.'}
            </p>
          </div>

          {/* Adım Göstergesi */}
          <div className="flex items-center justify-center mb-6">
            <div className={`w-3 h-3 rounded-full ${step === 1 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
            <div className="w-12 h-1 bg-gray-300"></div>
            <div className={`w-3 h-3 rounded-full ${step === 2 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
          </div>

          {/* Hata Mesajı */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {step === 1 ? (
            /* İlk Form */
            <form onSubmit={handleNext} className="space-y-4">
              <div>
                <input
                  type="text"
                  placeholder="TC Kimlik Numarası"
                  className={`w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600
                    ${validationErrors.tc ? 'border-red-500' : 'border-gray-300'}`}
                  maxLength="11"
                  value={formData.tc}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setFormData({ ...formData, tc: value });
                    if (validationErrors.tc) {
                      setValidationErrors({ ...validationErrors, tc: '' });
                    }
                  }}
                />
                {validationErrors.tc && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.tc}</p>
                )}
              </div>

              <div>
                <input
                  type="text"
                  placeholder="Plaka"
                  className={`w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600
                    ${validationErrors.plaka ? 'border-red-500' : 'border-gray-300'}`}
                  value={formData.plaka}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setFormData({ ...formData, plaka: value });
                    if (validationErrors.plaka) {
                      setValidationErrors({ ...validationErrors, plaka: '' });
                    }
                  }}
                />
                {validationErrors.plaka && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.plaka}</p>
                )}
              </div>

              <div>
                <input
                  type="text"
                  placeholder="Ruhsat Numarası"
                  className={`w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600
                    ${validationErrors.ruhsatNo ? 'border-red-500' : 'border-gray-300'}`}
                  value={formData.ruhsatNo}
                  onChange={(e) => {
                    setFormData({ ...formData, ruhsatNo: e.target.value });
                    if (validationErrors.ruhsatNo) {
                      setValidationErrors({ ...validationErrors, ruhsatNo: '' });
                    }
                  }}
                />
                {validationErrors.ruhsatNo && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.ruhsatNo}</p>
                )}
              </div>

              <button 
                type="submit" 
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition"
              >
                Devam Et
              </button>
            </form>
          ) : (
            /* İkinci Form */
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* İMM Seçimi */}
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <label className="text-gray-700 font-medium">İ.M.M teminatı ne kadar olsun?</label>
                  <div className="group relative">
                    <Info className="w-4 h-4 text-gray-400 cursor-help" />
                    <div className="invisible group-hover:visible absolute left-0 top-6 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                      İhtiyari Mali Mesuliyet (İMM), trafik sigortasının üzerinde ek teminat sağlar.
                    </div>
                  </div>
                </div>
                <div>
                  <div className="grid grid-cols-3 gap-3">
                    {['1.000.000', '3.000.000', '10.000.000+'].map((limit) => (
                      <button
                        key={limit}
                        type="button"
                        className={`p-3 border rounded-lg text-sm hover:border-blue-500 transition
                          ${formData.immLimit === limit ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                        onClick={() => {
                          setFormData({ ...formData, immLimit: limit });
                          if (validationErrors.immLimit) {
                            setValidationErrors({ ...validationErrors, immLimit: '' });
                          }
                        }}
                      >
                        {limit}
                      </button>
                    ))}
                  </div>
                  {validationErrors.immLimit && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.immLimit}</p>
                  )}
                </div>
              </div>

              {/* İkame Araç Seçimi */}
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <label className="text-gray-700 font-medium">İkame Araç Kaç Gün İstersiniz?</label>
                  <div className="group relative">
                    <Info className="w-4 h-4 text-gray-400 cursor-help" />
                    <div className="invisible group-hover:visible absolute left-0 top-6 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                      Aracınız servisteyken size tahsis edilecek araç süresi.
                    </div>
                  </div>
                </div>
                </div>

                <div>
                  <div className="grid grid-cols-3 gap-3">
                  {['7 gün', '15 gün', 'onarım süresi boyunca'].map((sure) => (
                    <button
                      key={sure}
                      type="button"
                      className={`p-3 border rounded-lg text-sm hover:border-blue-500 transition
                        ${formData.ikameArac === sure ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                      onClick={() => setFormData({ ...formData, ikameArac: sure })}
                    >
                      {sure}
                    </button>
                  ))}
                </div>
              </div>

              {/* Telefon */}
              <input
                type="tel"
                placeholder="Telefon Numarası"
                className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600"
                value={formData.telefon}
                onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
              />

              {/* KVKK */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={formData.kvkk}
                  onChange={(e) => setFormData({ ...formData, kvkk: e.target.checked })}
                  className="mt-1"
                />
                <label className="text-sm text-gray-600">
                  <Link to="/kvkk" className="text-gray-600 hover:text-blue-600 transition-colors">KVKK</Link>
                  {' ve '}
                  <Link to="/aydinlatma-metni" className="text-gray-600 hover:text-blue-600 transition-colors">site kullanım koşullarını</Link>
                  {' kabul ediyorum'}
                </label>
              </div>

              <button className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition">
                Fiyat Al
              </button>
            </form>
          )}
        </div>
      </div>
      <KaskoSeo />  
    </div>
  );
};

export default Kasko;